import { generateId } from "../../utils";
import { TableEditorConfig } from "../dto";
import { ROW } from "../contanst";

/**
 * 初始号表格配置，自动赋予默认值
 * @param config - 表格配置
 * @returns 表格配置
 */
export const initTableConfig = (
  config: TableEditorConfig
): TableEditorConfig => {
  return {
    ...config,
    formulas: config.formulas ?? [],
    loops: config.loops ?? [],
    rows: config.rows.map((row) => ({
      ...row,
      height: row.height ?? ROW.DEFAAULT_HEIGHT,
      id: row.id ?? generateId(),
      cells: row.cells
        .filter((cell) => cell.colSpan !== 0 && cell.rowSpan !== 0)
        .map((cell) => ({
          ...cell,
          id: cell.id ?? generateId(),
          colSpan: cell.colSpan ?? 1,
          rowSpan: cell.rowSpan ?? 1,
        })),
    })),
  };
};

/**
 * 根据一个可跨行跨列的表格模板配置，将其转换为最小单元的二维数组，并用 id 填充
 * @param template - 表格模板配置
 * @returns 一个二维数组，表示表格的最小单元格，每个单元格包含一个唯一的 id
 */
export const generateGrid = (config: TableEditorConfig): string[][] => {
  const { rows } = config;
  const grid = [];

  // 初始化二维数组
  for (let i = 0; i < rows.length; i++) {
    grid[i] = [];
  }

  // 遍历每一行
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    let colIndex = 0;

    // 遍历每个单元格
    for (const cell of row.cells) {
      // 跳过已经被其他单元格占用的位置
      while (grid[rowIndex][colIndex]) {
        colIndex++;
      }

      const rowSpan = cell.rowSpan ?? 1;
      const colSpan = cell.colSpan ?? 1;

      // 填充单元格id到grid中
      for (let i = 0; i < rowSpan; i++) {
        for (let j = 0; j < colSpan; j++) {
          if (grid[rowIndex + i]) {
            grid[rowIndex + i][colIndex + j] = cell.id;
          }
        }
      }

      colIndex += colSpan;
    }
  }

  return grid;
};
/**
 * 根据二维数组生成单元格矩形范围
 * @param grid - 二维数组，表示表格的最小单元格，每个单元格包含一个唯一的 id
 * @returns 一个对象，包含每个单元格的矩形范围，键为单元格id，值为一个对象，包含minRow、maxRow、minCol和maxCol四个属性
 */
export function generateCellRectsByGrid(grid: string[][]) {
  const cellRects: Record<
    string,
    { minRow: number; maxRow: number; minCol: number; maxCol: number }
  > = {};
  const visited = new Set(); // 记录已处理的单元格

  // 遍历每一行
  for (let row = 0; row < grid.length; row++) {
    // 遍历每一列
    for (let col = 0; col < grid[row].length; col++) {
      const cellId = grid[row][col];

      // 如果这个单元格已经处理过，跳过
      if (visited.has(`${row},${col}`)) continue;
      // 初始化这个单元格的矩形范围
      if (!cellRects[cellId]) {
        cellRects[cellId] = {
          minRow: row,
          minCol: col,
          maxRow: row,
          maxCol: col,
        };
      }
      // 检查向右扩展的列（计算colspan）
      let colSpan = 1;
      while (
        col + colSpan < grid[row].length &&
        grid[row][col + colSpan] === cellId
      ) {
        colSpan++;
      }

      // 检查向下扩展的行（计算rowspan）
      let rowSpan = 1;
      while (
        row + rowSpan < grid.length &&
        grid[row + rowSpan][col] === cellId
      ) {
        rowSpan++;
      }
      // 更新矩形范围
      cellRects[cellId].maxRow = row + rowSpan - 1;
      cellRects[cellId].maxCol = col + colSpan - 1;
      // 标记这个矩形范围内的所有单元格为已处理
      for (let r = row; r <= row + rowSpan - 1; r++) {
        for (let c = col; c <= col + colSpan - 1; c++) {
          visited.add(`${r},${c}`);
        }
      }
    }
  }
  return cellRects;
}
